import { useState } from 'react';
import { Download } from 'lucide-react';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { downloadXlsx } from './download';
import { chargeLabel, fmtChange, fmtMoney, fmtPct, periodLabel } from './format';
import { c, btn, card, chart, tbl } from '../../theme';

// One customer: what they were billed each month, line by line, and the
// baseline month at the scenario's rates.
//
// The flags and notes are read from the lines on screen and nothing else, so
// each one can be checked against the table beneath it. They are there to
// answer the question someone asks before calling a customer: is this bill
// normal, and what exactly goes up?
export default function CustomerDetail({ account, data, loading, error, scenario, categories, exportParams }) {
  const [exporting, setExporting] = useState(false);

  if (!account) return <section style={s.card}><div style={s.empty}>Choose a customer.</div></section>;
  if (loading && !data) return <section style={s.card}><LoadingSpinner /></section>;
  if (error) return <section style={s.card}><div style={s.error}>{error.message}</div></section>;
  if (!data) return null;

  const label = Object.fromEntries(categories.map((x) => [x.key, x.label]));
  const last = data.periods.length - 1;
  const { flags, notes } = readCustomer(data, scenario, label);
  // A customer whose first bill is this month is flagged once, in the header;
  // tagging every one of their lines "New this month" as well says nothing.
  const accountIsNew = data.totals.slice(0, last).every((t) => Math.abs(t) < 0.005);
  const maxBar = Math.max(1, ...data.totals.map(Math.abs), Math.abs(data.projected));

  return (
    <section style={s.card} aria-label={`Customer ${data.account}`}>
      <div style={s.head}>
        <div style={{ minWidth: 0 }}>
          <h2 style={s.h2}>{data.account}</h2>
          <div style={s.meta}>
            Company {data.company} · Customer #{data.customer_number}
            {data.cycle ? ` · Cycle ${data.cycle}` : ''}{data.price_list ? ` · Price list ${data.price_list}` : ''}
          </div>
          {flags.length > 0 && (
            <div style={s.flags}>
              {flags.map((f) => <span key={f.text} style={{ ...s.flag, background: f.bg, color: f.fg }}>{f.text}</span>)}
            </div>
          )}
        </div>
        <div style={s.figures}>
          <Figure label={`Billed, ${periodLabel(data.period, { short: true })}`} value={fmtMoney(data.current)} />
          <Figure label="Projected / month" value={scenario ? fmtMoney(data.projected) : '—'} />
          <Figure label="Change"
                  value={scenario ? <span style={changeColor(data.change)}>{fmtChange(data.change)}</span> : '—'}
                  sub={scenario ? `${fmtPct(data.pct)} · ${fmtChange(data.change * 12)} / yr` : null} />
        </div>
      </div>

      <div style={s.mid}>
        <div style={s.chartBox}>
          <div style={s.label}>Monthly bill</div>
          <div style={s.bars} role="img" aria-label={`Billed ${data.periods.map((p, i) => `${periodLabel(p)} ${fmtMoney(data.totals[i])}`).join(', ')}${scenario ? `; at the new rates ${fmtMoney(data.projected)}` : ''}`}>
            {data.periods.map((p, i) => (
              <Bar key={p} label={periodLabel(p, { short: true })} value={data.totals[i]} max={maxBar} color={i === last ? chart.emphasis : chart.deselected} />
            ))}
            {scenario && (
              <Bar label="New rates" value={data.projected} max={maxBar} color={chart.coVisible[1]} base={data.current} baseColor={chart.deselectedLight} />
            )}
          </div>
        </div>
        <div style={s.notes}>
          <div style={s.label}>What to know</div>
          {notes.map((n) => (
            <div key={n.text} style={s.noteRow}>
              <span style={{ ...s.dot, background: n.color }} aria-hidden="true" />
              <span>{n.text}</span>
            </div>
          ))}
          <div>
            <button type="button" style={s.exportBtn} disabled={exporting}
                    onClick={async () => {
                      setExporting(true);
                      await downloadXlsx(`/customer-billing/customers/${encodeURIComponent(data.account)}/export.xlsx`,
                        exportParams, `customer-billing-${data.account}.xlsx`);
                      setExporting(false);
                    }}>
              <Download size={12} /> {exporting ? 'Exporting…' : 'Export this customer'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th} rowSpan={2}>Charge</th>
              <th style={s.th} rowSpan={2}>Code</th>
              <th style={{ ...s.th, textAlign: 'center' }} colSpan={data.periods.length}>Billed</th>
              <th style={{ ...s.th, ...s.projHead, textAlign: 'center' }} colSpan={3}>{periodLabel(data.period, { short: true })} at new rates</th>
            </tr>
            <tr>
              {data.periods.map((p) => <th key={p} style={s.thR}>{periodLabel(p, { short: true })}</th>)}
              <th style={{ ...s.thR, ...s.projHead, ...s.divider }}>Current</th>
              <th style={{ ...s.thR, ...s.projHead }}>New</th>
              <th style={{ ...s.thR, ...s.projHead }}>Change</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((l) => {
              const tag = lineTag(l, last, scenario, accountIsNew);
              return (
                <tr key={`${l.description}|${l.size_code}|${l.charge_code}|${l.category}`} style={tbl.tr}>
                  <td style={s.td}>
                    {chargeLabel(l.description)}
                    {tag && <span style={{ ...s.tag, background: tag.bg, color: tag.fg }}>{tag.text}</span>}
                    <div style={s.cat}>{label[l.category] || l.category}{l.gl_account && l.gl_account !== 'NA' ? ` · GL ${l.gl_account}` : ''}</div>
                  </td>
                  <td style={{ ...s.td, color: c.textMuted, whiteSpace: 'nowrap' }}>{[l.size_code, l.charge_code].filter(Boolean).join(' / ') || '—'}</td>
                  {l.amounts.map((a, i) => (
                    <td key={data.periods[i]} style={{ ...s.tdR, color: a < 0 ? c.dangerText : a === 0 ? c.textFaint : c.textStrong }}>{a === 0 ? '—' : fmtMoney(a)}</td>
                  ))}
                  <td style={{ ...s.tdR, ...s.divider }}>{l.current === 0 ? '—' : fmtMoney(l.current)}</td>
                  <td style={{ ...s.tdR, fontWeight: 600 }}>{scenario ? (l.projected === 0 ? '—' : fmtMoney(l.projected)) : '—'}</td>
                  <td style={{ ...s.tdR, fontWeight: 600, ...changeColor(l.change) }}>{scenario ? fmtChange(l.change) : '—'}</td>
                </tr>
              );
            })}
            <tr style={s.totalRow}>
              <td style={{ ...s.td, fontWeight: 700 }} colSpan={2}>Total</td>
              {data.totals.map((t, i) => <td key={data.periods[i]} style={{ ...s.tdR, fontWeight: 700 }}>{fmtMoney(t)}</td>)}
              <td style={{ ...s.tdR, ...s.divider, fontWeight: 700 }}>{fmtMoney(data.current)}</td>
              <td style={{ ...s.tdR, fontWeight: 700 }}>{scenario ? fmtMoney(data.projected) : '—'}</td>
              <td style={{ ...s.tdR, fontWeight: 700, ...changeColor(data.change) }}>{scenario ? fmtChange(data.change) : '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Reading a customer ───────────────────────────────────────────────────────
// Exported for the tests: these sentences are what someone acts on, and each
// is a rule about the lines that can be got wrong without anything looking off.
export function readCustomer(data, scenario, label) {
  const flags = [];
  const notes = [];
  const n = data.periods.length;
  const billedMonths = data.totals.filter((t) => Math.abs(t) >= 0.005).length;
  const firstBilled = data.totals.findIndex((t) => Math.abs(t) >= 0.005);

  if (n > 1 && firstBilled === n - 1) {
    flags.push({ text: `New: first billed ${periodLabel(data.period)}`, bg: c.infoTint, fg: c.info });
    notes.push({ color: chart.coVisible[1], text: 'Only one month of history. Check this month is a normal bill before reading the change as the customer\'s monthly increase.' });
  } else if (billedMonths > 1) {
    const vals = data.totals.filter((t) => Math.abs(t) >= 0.005);
    const spread = (Math.max(...vals) - Math.min(...vals)) / Math.max(Math.abs(data.current), 0.01);
    if (spread < 0.02) flags.push({ text: `Same bill all ${billedMonths} months`, bg: c.successTint, fg: c.successText });
    else flags.push({ text: `Bill varied ${Math.round(spread * 100)}% over ${billedMonths} months`, bg: c.warningTint, fg: c.warningText });
  }

  const lateFees = data.lines.filter((l) => l.category === 'late_fee').reduce((sum, l) => sum + l.amounts.reduce((a, b) => a + b, 0), 0);
  if (lateFees >= 0.005) {
    flags.push({ text: `Late fees ${fmtMoney(lateFees)} in ${n} months`, bg: c.dangerTint, fg: c.dangerText });
  }
  if (scenario && data.change >= 250) flags.push({ text: 'Up $250+ a month', bg: c.warningTint, fg: c.warningText });

  if (scenario) {
    if (Math.abs(data.change) < 0.005) {
      notes.unshift({ color: chart.other, text: 'This scenario changes nothing on this customer\'s bill.' });
    } else {
      // What the change is made of, largest first, by category.
      const byCat = new Map();
      for (const l of data.lines) if (Math.abs(l.change) >= 0.005) byCat.set(l.category, (byCat.get(l.category) || 0) + l.change);
      const parts = [...byCat.entries()].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
        .map(([k, v]) => `${fmtChange(v)} ${String(label[k] || k).toLowerCase()}`);
      const covered = data.lines.filter((l) => Math.abs(l.change) >= 0.005).reduce((sum, l) => sum + l.current, 0);
      notes.unshift({
        color: chart.coVisible[0],
        text: `${data.change > 0 ? 'Goes up' : 'Goes down'} ${fmtChange(data.change).replace(/^[+−]/, '')} a month: ${parts.join(', ')}. `
          + (data.current > 0 ? `${Math.round((covered / data.current) * 100)}% of the bill is in categories the scenario changes.` : ''),
      });
      if (lateFees >= 0.005) notes.push({ color: c.danger, text: `Already paying late (${fmtMoney(lateFees)} in late fees). Worth a call before the change.` });
    }
  }

  const started = data.lines.filter((l) => lineTag(l, n - 1, scenario)?.kind === 'new').length;
  const stopped = data.lines.filter((l) => lineTag(l, n - 1, scenario)?.kind === 'stopped').length;
  if (firstBilled < n - 1 && (started || stopped)) {
    notes.push({ color: chart.other, text: `${[started && `${started} charge${started === 1 ? '' : 's'} started`, stopped && `${stopped} stopped`].filter(Boolean).join(', ')} during these months — see the tags below.` });
  }
  if (!notes.length) notes.push({ color: chart.other, text: 'Nothing unusual in these months.' });
  return { flags, notes };
}

function lineTag(l, last, scenario, accountIsNew = false) {
  const earlier = l.amounts.slice(0, last);
  const now = l.amounts[last];
  if (!accountIsNew && Math.abs(now) >= 0.005 && earlier.length && earlier.every((a) => Math.abs(a) < 0.005)) {
    return { kind: 'new', text: 'New this month', bg: c.infoTint, fg: c.info };
  }
  if (Math.abs(now) < 0.005 && earlier.some((a) => Math.abs(a) >= 0.005)) {
    return { kind: 'stopped', text: 'Not billed this month', bg: c.canvas, fg: c.textStrong };
  }
  if (now <= -0.005) return { kind: 'credit', text: 'Credit', bg: c.infoTint, fg: c.info };
  if (scenario && Math.abs(now) >= 0.005 && Math.abs(l.change) < 0.005) return { kind: 'unchanged', text: 'Not in scenario', bg: c.canvas, fg: c.textMuted };
  return null;
}

function Figure({ label, value, sub }) {
  return (
    <div>
      <div style={s.label}>{label}</div>
      <div style={s.figValue}>{value}</div>
      {sub && <div style={s.figSub}>{sub}</div>}
    </div>
  );
}

// A bar for one month. `base` draws the current bill inside the projected one,
// so the part that is the increase stands on top of what they pay today.
function Bar({ label, value, max, color, base = null, baseColor }) {
  const h = (v) => `${Math.max(2, Math.round((Math.abs(v) / max) * 110))}px`;
  return (
    <div style={s.barCol}>
      <div style={s.barVal}>{fmtMoney(value)}</div>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '112px' }}>
        {base !== null && value > base ? (
          <>
            <div style={{ height: h(value - base), background: color, borderRadius: '3px 3px 0 0' }} />
            <div style={{ height: h(base), background: baseColor }} />
          </>
        ) : (
          <div style={{ height: h(value), background: color, borderRadius: '3px 3px 0 0' }} />
        )}
      </div>
      <div style={s.barLabel}>{label}</div>
    </div>
  );
}

const changeColor = (n) => (n >= 0.005 ? { color: c.successText } : n <= -0.005 ? { color: c.dangerText } : { color: c.textFaint });

const s = {
  card:     { ...card.sm, overflow: 'hidden' },
  head:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '18px', flexWrap: 'wrap', padding: '14px 18px', borderBottom: `1px solid ${c.border}` },
  h2:       { margin: 0, fontSize: '18px', fontWeight: 650, color: c.text },
  meta:     { fontSize: '12px', color: c.textMuted, marginTop: '3px' },
  flags:    { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' },
  flag:     { fontSize: '11.5px', fontWeight: 500, padding: '2px 9px', borderRadius: '999px' },
  figures:  { display: 'flex', gap: '24px', flexWrap: 'wrap' },
  figValue: { fontSize: '18px', fontWeight: 700, color: c.text, marginTop: '3px', fontVariantNumeric: 'tabular-nums' },
  figSub:   { fontSize: '11px', color: c.textMuted, marginTop: '1px' },
  label:    { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted },
  mid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', borderBottom: `1px solid ${c.border}` },
  chartBox: { padding: '12px 18px', borderRight: `1px solid ${c.border}` },
  bars:     { display: 'flex', gap: '12px', alignItems: 'flex-end', marginTop: '8px' },
  barCol:   { flex: 1, minWidth: '52px', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '4px', textAlign: 'center' },
  barVal:   { fontSize: '10.5px', fontWeight: 600, color: c.textStrong, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  barLabel: { fontSize: '11px', color: c.textMuted },
  notes:    { padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '8px' },
  noteRow:  { display: 'flex', gap: '8px', fontSize: '12.5px', color: c.textStrong, lineHeight: 1.45 },
  dot:      { width: '7px', height: '7px', borderRadius: '50%', marginTop: '6px', flexShrink: 0 },
  exportBtn:{ ...btn.secondaryXs, marginTop: '2px' },
  table:    { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:       { ...tbl.thSm, whiteSpace: 'nowrap' },
  thR:      { ...tbl.thRSm },
  projHead: { background: c.infoTint },
  divider:  { borderLeft: `1px solid ${c.border}` },
  td:       { ...tbl.tdSm, verticalAlign: 'top' },
  tdR:      { ...tbl.tdRSm, verticalAlign: 'top' },
  cat:      { fontSize: '11px', color: c.textFaint, marginTop: '1px' },
  tag:      { display: 'inline-block', fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: '999px', marginLeft: '6px', whiteSpace: 'nowrap' },
  totalRow: { background: c.surfaceSunk, borderTop: `1px solid ${c.border}` },
  empty:    { padding: '48px 18px', textAlign: 'center', fontSize: '12.5px', color: c.textMuted },
  error:    { padding: '14px', color: c.dangerText, fontSize: '12.5px' },
};
